import {
  Email,
  MouseOutlined,
  VisibilityOutlined,
  AccessTime,
  ExpandMore,
  ExpandLess,
  Message,
  LinkedIn,
  PersonSearch,
  ConnectWithoutContact,
  Sms,
  Event,
  EventAvailable,
  RecordVoiceOver,
  Error as ErrorIcon,
  SwapHoriz,
  FilterList,
  Reply,
  Phone,
  SentimentSatisfiedAlt,
  SentimentNeutral,
  SentimentDissatisfied,
  Star,
  StarHalf,
  StarOutline,
  Download,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  Collapse,
  Button,
  TablePagination,
  Avatar,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import axios from "axios";
import DOMPurify from "dompurify";
import moment from "moment";
import { useEffect, useState } from "react";
import { hostname, sanitizeFilename } from "../../../utils";
import LoadingButton from "../../../components/LoadingButton";

interface CallIntelligence {
  sentiment?: string;
  outcome?: string;
  outcome_summary?: string;
  meeting_booked?: boolean;
  meeting_details?: string;
  interest_level?: string;
  objections?: string[];
  key_topics?: string[];
  follow_up_actions?: string[];
  call_quality?: string;
}

interface TimelineEvent {
  type: string;
  timestamp: string;
  subject?: string;
  executionId?: string;
  stepName?: string;
  content?: string;
  openCount?: number;
  clickCount?: number;
  ipAddress?: string;
  userAgent?: string;
  referrer?: string;
  clickedUrl?: string;
  platform?: string;
  actionType?: string;
  failureMessage?: string;
  previousStatus?: string;
  newStatus?: string;
  reason?: string;
  isBot?: boolean;
  botProvider?: string;
  callIntelligence?: CallIntelligence;
  callDuration?: number;
  callResult?: string;
  transcript?: string;
  member?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    linkedinUrl?: string;
  };
}

interface SequenceActivityViewProps {
  companyId: string;
  sequence: { id: string; name: string } | null;
  getToken: () => Promise<string>;
  showTitle?: boolean;
}

const EVENT_TYPE_OPTIONS = [
  { value: "all", label: "All Events" },
  { value: "email_opened", label: "Email Opened" },
  { value: "email_clicked", label: "Email Clicked" },
  { value: "email_reply", label: "Email Reply" },
  { value: "voice_call", label: "Voice Call" },
  { value: "schedule_view", label: "Meeting Page Viewed" },
  { value: "schedule_booking", label: "Meeting Booked" },
];

export default function SequenceActivityView({
  companyId,
  sequence,
  getToken,
  showTitle = true,
}: SequenceActivityViewProps) {
  const [loading, setLoading] = useState(false);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [filterBots, setFilterBots] = useState(false);
  const [expandedContent, setExpandedContent] = useState<{
    [key: string]: boolean;
  }>({});
  const [exportLoading, setExportLoading] = useState(false);

  const exportActivity = async () => {
    if (!sequence) return;

    setExportLoading(true);
    try {
      const currentToken = await getToken();
      const params = new URLSearchParams({
        companyId,
        filterBots: filterBots ? "true" : "false",
      });
      if (eventTypeFilter !== "all") {
        params.append("eventType", eventTypeFilter);
      }

      const response = await fetch(
        `${hostname}/sequences/${sequence.id}/activity/export?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      if (!response.ok) {
        throw new Error("Export failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = sanitizeFilename(
        `${sequence.name || "sequence"}_activity_export.csv`,
        "activity_export.csv"
      );
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting activity:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const loadActivity = async () => {
    if (!sequence) return;

    setLoading(true);
    try {
      const currentToken = await getToken();
      const response = await axios.get(
        `${hostname}/sequences/${sequence.id}/activity`,
        {
          params: {
            companyId,
            page: page + 1,
            pageSize: rowsPerPage,
            eventType: eventTypeFilter !== "all" ? eventTypeFilter : undefined,
            filterBots: filterBots ? "true" : "false",
          },
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      setTimeline(response.data.timeline);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error("Error loading activity:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sequence) {
      loadActivity();
    }
  }, [sequence, page, rowsPerPage, eventTypeFilter, filterBots]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEventTypeFilterChange = (event: SelectChangeEvent) => {
    setEventTypeFilter(event.target.value);
    setPage(0);
  };

  const handleFilterBotsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterBots(event.target.checked);
    setPage(0);
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case "email_sent":
        return <Email sx={{ fontSize: 16 }} />;
      case "email_failed":
        return <ErrorIcon sx={{ fontSize: 16 }} />;
      case "email_opened":
      case "tracking_open":
        return <VisibilityOutlined sx={{ fontSize: 16 }} />;
      case "email_reply":
        return <Reply sx={{ fontSize: 16 }} />;
      case "email_clicked":
      case "tracking_click":
        return <MouseOutlined sx={{ fontSize: 16 }} />;
      case "sms_sent":
        return <Sms sx={{ fontSize: 16 }} />;
      case "sms_failed":
        return <ErrorIcon sx={{ fontSize: 16 }} />;
      case "voice_message_sent":
        return <RecordVoiceOver sx={{ fontSize: 16 }} />;
      case "voice_message_failed":
        return <ErrorIcon sx={{ fontSize: 16 }} />;
      case "linkedin_profile_visit":
      case "linkedin_profile_visited":
        return <PersonSearch sx={{ fontSize: 16 }} />;
      case "linkedin_connection_request":
      case "linkedin_connection_sent":
        return <ConnectWithoutContact sx={{ fontSize: 16 }} />;
      case "linkedin_message":
      case "linkedin_message_sent":
        return <Message sx={{ fontSize: 16 }} />;
      case "linkedin_inmails":
      case "inmail_executed":
        return <Email sx={{ fontSize: 16 }} />;
      case "Schedule View":
      case "schedule_view":
        return <Event sx={{ fontSize: 16 }} />;
      case "Schedule Booking":
      case "schedule_booking":
        return <EventAvailable sx={{ fontSize: 16 }} />;
      case "status_change":
        return <SwapHoriz sx={{ fontSize: 16 }} />;
      case "voice_call_completed":
        return <Phone sx={{ fontSize: 16 }} />;
      default:
        if (type.endsWith("_failed")) {
          return <ErrorIcon sx={{ fontSize: 16 }} />;
        }
        return type.startsWith("linkedin_") ? (
          <LinkedIn sx={{ fontSize: 16 }} />
        ) : (
          <AccessTime sx={{ fontSize: 16 }} />
        );
    }
  };

  const getEventLabel = (type: string) => {
    switch (type) {
      case "email_sent":
        return "Email Sent";
      case "email_failed":
        return "Email Failed";
      case "email_opened":
        return "Email Opened";
      case "email_clicked":
        return "Email Clicked";
      case "email_reply":
        return "Reply Received";
      case "sms_sent":
        return "SMS Sent";
      case "sms_failed":
        return "SMS Failed";
      case "voice_message_sent":
        return "Voice Message Sent";
      case "voice_message_failed":
        return "Voice Message Failed";
      case "tracking_open":
        return "Opened";
      case "tracking_click":
        return "Clicked Link";
      case "linkedin_profile_visit":
      case "linkedin_profile_visited":
        return "Profile Visit";
      case "linkedin_connection_request":
      case "linkedin_connection_sent":
        return "Connection Request";
      case "linkedin_message":
      case "linkedin_message_sent":
        return "Message Sent";
      case "linkedin_inmails":
      case "inmail_executed":
        return "InMail Sent";
      case "Schedule View":
      case "schedule_view":
        return "Viewed Meeting Page";
      case "Schedule Booking":
      case "schedule_booking":
        return "Booked Meeting";
      case "status_change":
        return "Status Changed";
      case "voice_call_completed":
        return "Voice Call";
      default:
        if (type.endsWith("_failed")) {
          return type
            .replace("_failed", " Failed")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
        }
        if (type.endsWith("_executed")) {
          return type
            .replace("_executed", "")
            .replace(/_/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());
        }
        return type.startsWith("linkedin_")
          ? type
              .replace("linkedin_", "")
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase())
          : type
              .replace(/_/g, " ")
              .replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  const getEventColor = (type: string): any => {
    if (type.endsWith("_failed")) {
      return "error";
    }
    switch (type) {
      case "email_sent":
        return "primary";
      case "email_opened":
      case "tracking_open":
        return "primary";
      case "email_clicked":
      case "tracking_click":
        return "info";
      case "email_reply":
        return "primary";
      case "sms_sent":
        return "primary";
      case "voice_message_sent":
        return "primary";
      case "linkedin_profile_visit":
      case "linkedin_profile_visited":
        return "secondary";
      case "linkedin_connection_request":
      case "linkedin_connection_sent":
        return "warning";
      case "linkedin_message":
      case "linkedin_message_sent":
      case "linkedin_inmails":
      case "inmail_executed":
        return "info";
      case "Schedule View":
      case "schedule_view":
        return "info";
      case "Schedule Booking":
      case "schedule_booking":
        return "primary";
      case "status_change":
        return "warning";
      case "voice_call_completed":
        return "primary";
      default:
        return type.startsWith("linkedin_") ? "primary" : "default";
    }
  };

  const formatCallDuration = (seconds?: number) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const getSentimentIcon = (sentiment?: string) => {
    switch (sentiment) {
      case "positive":
        return <SentimentSatisfiedAlt sx={{ fontSize: 14 }} />;
      case "negative":
        return <SentimentDissatisfied sx={{ fontSize: 14 }} />;
      default:
        return <SentimentNeutral sx={{ fontSize: 14 }} />;
    }
  };

  const getInterestIcon = (level?: string) => {
    switch (level) {
      case "high":
        return <Star sx={{ fontSize: 14 }} />;
      case "medium":
        return <StarHalf sx={{ fontSize: 14 }} />;
      default:
        return <StarOutline sx={{ fontSize: 14 }} />;
    }
  };

  const getOutcomeColor = (outcome?: string): any => {
    switch (outcome) {
      case "meeting_booked":
        return "primary";
      case "meeting_requested":
      case "interested":
        return "info";
      case "callback_requested":
        return "warning";
      case "not_interested":
      case "wrong_number":
        return "error";
      default:
        return "default";
    }
  };

  const formatOutcome = (outcome?: string) => {
    if (!outcome) return "Unknown";
    return outcome
      .replace(/_/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const toggleContent = (executionId: string) => {
    setExpandedContent((prev) => ({
      ...prev,
      [executionId]: !prev[executionId],
    }));
  };

  const getMemberDisplayName = (member: TimelineEvent["member"]) => {
    if (!member) return "Unknown";
    const name = `${member.firstName || ""} ${member.lastName || ""}`.trim();
    return name || member.email || "Unknown";
  };

  const getMemberInitials = (member: TimelineEvent["member"]) => {
    if (!member) return "?";
    const first = member.firstName?.[0] || "";
    const last = member.lastName?.[0] || "";
    return (first + last).toUpperCase() || member.email?.[0]?.toUpperCase() || "?";
  };

  if (!sequence) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Save the sequence first to view activity.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {showTitle && <Typography variant="h6">Activity Timeline</Typography>}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Event Type</InputLabel>
            <Select
              value={eventTypeFilter}
              label="Event Type"
              onChange={handleEventTypeFilterChange}
              startAdornment={<FilterList sx={{ mr: 1, color: "action.active" }} />}
            >
              {EVENT_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Tooltip title="Filter out automated scans from email security services (Microsoft, Google, Proofpoint, etc.)">
            <FormControlLabel
              control={
                <Switch
                  checked={filterBots}
                  onChange={handleFilterBotsChange}
                  size="small"
                />
              }
              label={<Typography variant="body2">Hide Bots</Typography>}
            />
          </Tooltip>
        </Box>
        <LoadingButton
          size="small"
          color="inherit"
          startIcon={<Download />}
          onClick={exportActivity}
          loading={exportLoading}
          disabled={timeline.length === 0}
        >
          Export
        </LoadingButton>
      </Box>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {!loading && timeline.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ py: 4, textAlign: "center" }}
        >
          No activity found for this sequence yet.
        </Typography>
      )}

      {timeline.length > 0 && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableBody>
                {timeline.map((event, index) => (
                  <TableRow
                    key={`${event.executionId || event.timestamp}-${index}`}
                  >
                    <TableCell>
                      <Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            flexWrap: "wrap",
                          }}
                        >
                          <Chip
                            icon={getEventIcon(event.type)}
                            label={getEventLabel(event.type)}
                            size="small"
                            color={getEventColor(event.type)}
                          />
                          {event.stepName && (
                            <Typography variant="body2" fontWeight={500}>
                              {event.stepName}
                            </Typography>
                          )}
                        </Box>

                        {event.subject && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {event.subject}
                          </Typography>
                        )}

                        {event.type === "status_change" && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                          >
                            {event.previousStatus} → {event.newStatus}
                            {event.reason && ` (${event.reason})`}
                          </Typography>
                        )}

                        {event.failureMessage && (
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{ mt: 0.5 }}
                          >
                            {event.failureMessage}
                          </Typography>
                        )}

                        {/* Call Intelligence Display */}
                        {event.type === "voice_call_completed" &&
                          event.callIntelligence && (
                            <Box sx={{ mt: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  flexWrap: "wrap",
                                  mb: 0.5,
                                }}
                              >
                                <Chip
                                  icon={getSentimentIcon(
                                    event.callIntelligence.sentiment
                                  )}
                                  label={
                                    event.callIntelligence.sentiment
                                      ? event.callIntelligence.sentiment
                                          .charAt(0)
                                          .toUpperCase() +
                                        event.callIntelligence.sentiment.slice(
                                          1
                                        )
                                      : "Unknown"
                                  }
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 24 }}
                                />
                                <Chip
                                  label={formatOutcome(
                                    event.callIntelligence.outcome
                                  )}
                                  size="small"
                                  color={getOutcomeColor(
                                    event.callIntelligence.outcome
                                  )}
                                  sx={{ height: 24 }}
                                />
                                {event.callIntelligence.interest_level && (
                                  <Chip
                                    icon={getInterestIcon(
                                      event.callIntelligence.interest_level
                                    )}
                                    label={`${event.callIntelligence.interest_level} interest`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 24 }}
                                  />
                                )}
                                {event.callIntelligence.meeting_booked && (
                                  <Chip
                                    icon={<EventAvailable sx={{ fontSize: 14 }} />}
                                    label="Meeting Booked"
                                    size="small"
                                    color="primary"
                                    sx={{ height: 24 }}
                                  />
                                )}
                                {event.callDuration && (
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {formatCallDuration(event.callDuration)}
                                  </Typography>
                                )}
                              </Box>
                              {event.callIntelligence.outcome_summary && (
                                <Tooltip
                                  title={
                                    event.transcript ? (
                                      <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                                        <Typography
                                          variant="caption"
                                          sx={{ whiteSpace: "pre-wrap", fontFamily: "monospace" }}
                                        >
                                          {event.transcript}
                                        </Typography>
                                      </Box>
                                    ) : (
                                      "No transcript available"
                                    )
                                  }
                                  arrow
                                  placement="bottom-start"
                                  slotProps={{
                                    tooltip: {
                                      sx: {
                                        maxWidth: 500,
                                        bgcolor: "background.paper",
                                        color: "text.primary",
                                        border: 1,
                                        borderColor: "divider",
                                        boxShadow: 3,
                                        p: 1.5,
                                      },
                                    },
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontStyle: "italic", cursor: "pointer" }}
                                  >
                                    "{event.callIntelligence.outcome_summary}"
                                  </Typography>
                                </Tooltip>
                              )}
                              {event.callIntelligence.meeting_details && (
                                <Typography
                                  variant="body2"
                                  color="primary.main"
                                  sx={{ mt: 0.5 }}
                                >
                                  {event.callIntelligence.meeting_details}
                                </Typography>
                              )}
                            </Box>
                          )}

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mt: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {moment(event.timestamp).format(
                              "MMM D, YYYY h:mm A"
                            )}
                          </Typography>
                          {event.openCount && event.openCount > 1 && (
                            <Typography variant="caption" color="primary.main">
                              Opened {event.openCount} times
                            </Typography>
                          )}
                          {event.clickCount && event.clickCount > 1 && (
                            <Typography variant="caption" color="info.main">
                              Clicked {event.clickCount} times
                            </Typography>
                          )}
                          {event.ipAddress && (
                            <Typography variant="caption" color="text.secondary">
                              IP: {event.ipAddress}
                              {event.isBot && (
                                <Tooltip title={`Bot traffic from ${event.botProvider || "unknown provider"}`}>
                                  <Chip
                                    label={event.botProvider || "Bot"}
                                    size="small"
                                    color="warning"
                                    sx={{ ml: 1, height: 18, fontSize: "0.65rem" }}
                                  />
                                </Tooltip>
                              )}
                            </Typography>
                          )}
                        </Box>

                        {event.clickedUrl && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              display: "block",
                              wordBreak: "break-all",
                            }}
                          >
                            Clicked:{" "}
                            <Link
                              href={/^https?:\/\/[a-zA-Z0-9]/.test(event.clickedUrl) ? event.clickedUrl : "#"}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {event.clickedUrl}
                            </Link>
                          </Typography>
                        )}

                        {event.userAgent && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                              mt: 0.5,
                              display: "block",
                              maxWidth: 400,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                            title={event.userAgent}
                          >
                            {event.userAgent}
                          </Typography>
                        )}

                        {event.content &&
                          (event.type === "email_sent" ||
                            event.type === "sms_sent" ||
                            event.type === "linkedin_message_sent") && (
                            <Button
                              size="small"
                              onClick={() => toggleContent(event.executionId!)}
                              startIcon={
                                expandedContent[event.executionId!] ? (
                                  <ExpandLess />
                                ) : (
                                  <ExpandMore />
                                )
                              }
                              sx={{ mt: 1 }}
                            >
                              {expandedContent[event.executionId!]
                                ? "Hide Content"
                                : "View Content"}
                            </Button>
                          )}

                        {event.content && event.executionId && (
                          <Collapse in={expandedContent[event.executionId]}>
                            <Box
                              sx={{
                                mt: 1,
                                p: 2,
                                borderRadius: 1,
                                backgroundColor: "grey.50",
                                "& img": { maxWidth: "100%" },
                                "& a": { color: "primary.main" },
                              }}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  event.type === "sms_sent" ||
                                  event.type === "linkedin_message_sent"
                                    ? `<pre style="white-space: pre-wrap; margin: 0;">${event.content}</pre>`
                                    : event.content,
                                ),
                              }}
                            />
                          </Collapse>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell width={200}>
                      {event.member && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              fontSize: "0.875rem",
                              bgcolor: "primary.main",
                            }}
                          >
                            {getMemberInitials(event.member)}
                          </Avatar>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="body2"
                              fontWeight={500}
                              noWrap
                              sx={{ maxWidth: 140 }}
                            >
                              {getMemberDisplayName(event.member)}
                            </Typography>
                            {event.member.email && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ display: "block", maxWidth: 140 }}
                              >
                                {event.member.email}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </>
      )}
    </Box>
  );
}
